import phpDate from '../../common/date-formatter/pure-date-format';
import { getUserFormat } from '../../common/user-formats/userFormats.js';

const DEFAULT_DATE_FORMAT = 'j M, Y';
const DEFAULT_TIME_FORMAT = 'H:i';

const getUserDateFormat = () => {
  return getUserFormat('twigDateFormat') || DEFAULT_DATE_FORMAT;
};

const getUserTimeFormat = () => {
  return getUserFormat('twigTimeFormat') || DEFAULT_TIME_FORMAT;
};

/**
 * Safely parse a date value into a Date object.
 * Handles Unix timestamps (number), YYYY-MM-DD strings (without timezone drift),
 * datetime strings, and Date objects. Returns null for invalid values.
 */
const parseDate = (value) => {
  if (value == null) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value * 1000);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const num = Number(value);
    if (!isNaN(num) && num > 1000000000) {
      const date = new Date(num * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    // YYYY-MM-DD — parse component-wise to avoid timezone drift
    const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      return new Date(
        parseInt(dateMatch[1], 10),
        parseInt(dateMatch[2], 10) - 1,
        parseInt(dateMatch[3], 10)
      );
    }

    // YYYY-MM-DD HH:MM(:SS) or ISO with T separator
    const dtMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (dtMatch) {
      return new Date(
        parseInt(dtMatch[1], 10),
        parseInt(dtMatch[2], 10) - 1,
        parseInt(dtMatch[3], 10),
        parseInt(dtMatch[4], 10),
        parseInt(dtMatch[5], 10),
        dtMatch[6] ? parseInt(dtMatch[6], 10) : 0
      );
    }

    // Fallback to native parsing
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

/**
 * Format a date-only value using the user's preferred date format.
 * Returns '-' for null/undefined/invalid values.
 */
export const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return '-';
  return phpDate(date, getUserDateFormat());
};

/**
 * Format a date+time value using the user's preferred date and time formats.
 * Returns '-' for null/undefined/invalid values.
 */
export const formatDateTime = (value) => {
  const date = parseDate(value);
  if (!date) return '-';
  return phpDate(date, `${getUserDateFormat()} ${getUserTimeFormat()}`);
};
